import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Box, Chip, Stack, Typography, useTheme } from '@mui/material';

import { TalentDTO, useGetOrganizationStructure } from '@/api/generated';
import PencilIcon from '@/assets/icons/pencil.svg?react';
import DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import DropdownMenuItem from '@/components/DropdownMenu/DropdownMenuItem';
import LocationMultiDisplay from '@/components/fields/Location/LocationMultiDisplay';
import { buildFlatStructure } from '@/components/fields/Location/locationTreeUtils';
import HeaderProperty from '@/components/HeaderProperty';
import { UserAvatar } from '@/components/UserAvatar';
import OfficeDisplay from '@/containers/Organizations/OfficeDisplay';
import { useIsMidotUser } from '@/hooks/permissions/useIsMidotUser';
import { useFormattedDatetime } from '@/hooks/useFormattedDatetime';
import { TalentModalEditSection, TalentModalKey } from '@/views/TalentEditInfoModal/constants';

type TalentHeaderProps = { talent?: TalentDTO };

const TalentHeader = ({ talent }: TalentHeaderProps) => {
  const { spacing } = useTheme();
  const { t } = useTranslation(['talents', 'filters', 'common']);
  const { formatDatetime } = useFormattedDatetime();
  const { isMidotUser } = useIsMidotUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const openEditInfoModal = () => {
    searchParams.set(TalentModalKey, TalentModalEditSection.BASIC_INFO);
    setSearchParams(searchParams);
  };

  // TODO: remove mock
  const { data: orgStructure } = useGetOrganizationStructure(talent?.organization?.slug ?? '', {
    query: {
      enabled: !!talent?.organization?.slug
    }
  });
  const mockLocations = useMemo(() => {
    if (!orgStructure) return [];
    const flatOrgStructure = buildFlatStructure(orgStructure);

    return flatOrgStructure?.slice(1, 4) || [];
  }, [orgStructure]);

  return (
    <Stack
      direction="row"
      sx={{
        padding: spacing(4)
      }}
    >
      <Stack direction="row" maxWidth="70%" m={0}>
        <UserAvatar user={talent} variant="talent-large" />
        <Stack direction="column" marginInlineStart={spacing(2)}>
          <Stack direction="row" alignItems="center">
            <Typography component="h3" variant="h3">
              {t('header.user.fullName', {
                firstName: talent?.firstName,
                lastName: talent?.lastName,
                ns: 'common'
              })}
            </Typography>
            {talent?.type && (
              <Chip
                sx={{ marginInlineStart: spacing(1) }}
                component="span"
                variant="tag"
                label={t(`filters.options.talentTypes.${talent.type.toLowerCase()}`, { ns: 'filters' })}
              />
            )}
          </Stack>
          <Stack component="dl" direction="row" flexWrap="wrap" sx={{ margin: 0 }}>
            <HeaderProperty label={t(`layouts.talents.columns.email`)} fallback="-">
              {talent?.email}
            </HeaderProperty>
            <HeaderProperty label={t(`layouts.talents.columns.phone`)} fallback="-">
              {talent?.phone}
            </HeaderProperty>
            <HeaderProperty label={t(`layouts.talents.columns.idNumber`)} fallback="-">
              {talent?.idNumber}
            </HeaderProperty>
            {isMidotUser && (
              <HeaderProperty label={t(`layouts.talents.columns.id`)} fallback="-">
                {talent?.id}
              </HeaderProperty>
            )}
            {isMidotUser && (
              <HeaderProperty
                label={t(`layouts.talents.columns.offices`, { count: talent?.organization?.offices?.length ?? 0 })}
              >
                <OfficeDisplay variant="body2" value={talent?.organization?.offices} fallback="-" />
              </HeaderProperty>
            )}
            {talent?.organization?.slug && (
              <HeaderProperty label={t(`layouts.talents.columns.location`)}>
                <Box>
                  <LocationMultiDisplay
                    organization={talent.organization}
                    locations={talent.locations || mockLocations}
                    outlined
                  />
                </Box>
              </HeaderProperty>
            )}
          </Stack>
        </Stack>
      </Stack>
      <Stack direction="row" sx={{ alignSelf: 'start', marginInlineStart: 'auto' }}>
        <Typography
          component="time"
          dateTime={talent?.lastActivity}
          color="grey.700"
          variant="body3"
          sx={{
            alignSelf: 'start',
            marginInlineEnd: 3,
            lineHeight: spacing(3),
            verticalAlign: 'middle'
          }}
        >
          {t('layouts.talent.lastActivity', { date: talent?.lastActivity ? formatDatetime(talent.lastActivity) : '-' })}
        </Typography>
        <DropdownMenu>
          <DropdownMenuItem icon={<PencilIcon />} text={t('layouts.talent.editInfo')} onClick={openEditInfoModal} />
        </DropdownMenu>
      </Stack>
    </Stack>
  );
};

export default TalentHeader;
